// Shared Button - Using Radix Slot for composability
import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'neutral'
  size?: 'sm' | 'md' | 'lg'
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        className={clsx(
          // Base styles
          'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-primary',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'active:scale-[0.98]',

          // Size variants
          {
            sm: 'h-10 px-4 text-sm',
            md: 'h-12 px-5 text-base',
            lg: 'h-14 px-6 text-lg',
          }[size],

          // Color variants using brand colors
          {
            primary: 'bg-brand-primary text-[var(--brand-primary-text)] hover:bg-brand-primary-hover active:bg-brand-primary-dark',
            secondary: 'bg-brand-primary-lighter text-brand-primary-dark hover:bg-brand-primary-light active:bg-brand-primary-lighter',
            outline: 'border-2 border-brand-primary text-brand-primary bg-transparent hover:bg-brand-primary-lightest active:bg-brand-primary-lighter',
            ghost: 'text-brand-primary bg-transparent hover:bg-brand-primary-lightest active:bg-brand-primary-lighter',
            danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
            // Quiet, low-emphasis action (Done, Cancel) beside or instead of
            // the one brand-coloured button on a view (DEV-51010).
            neutral: 'border-2 border-gray-300 bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100',
          }[variant],

          className
        )}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'
